import type { AuditCloneQueryParams, AuditPatchAssigneesRequest } from '@arhs-fleetback/types/entities/audits';
import { InterventionStatus } from '@arhs-fleetback/types/entities/interventions';
import { generatePath } from 'react-router';

import { hasAccess } from 'components/common/utils/hasAccess';
import { PATH } from 'constants/RoutesConstants';
import type { MayBe } from 'modules/global/typing/common/common';
import type { BasicIntervention } from 'modules/interventions/types/projections/basic-intervention';

import { AUDIT_MANAGER_ROLES, CloneOrigin, DELETABLE_AUDIT_STATUSES } from './constants';
import type { AuditResponse } from './types/resources/questions/responses/audit-response';
import type { AuditSection } from './types/resources/sections/audit-section';
import type { StoredAudit } from './types/stored-audit';

/** Get all sections from an audit. */
const getAllSections = (audit: MayBe<StoredAudit>): AuditSection[] => audit?.sections ?? [];

/** Get the index of a section by its ID. Returns -1 if not found. */
const getSectionIndex = (audit: MayBe<StoredAudit>, sectionId: string): number =>
  getAllSections(audit).findIndex(section => section.id === sectionId);

/** Get all responses from all sections (flattened). */
const getAllAuditResponses = (audit: MayBe<StoredAudit>): AuditResponse[] =>
  getAllSections(audit).flatMap(section => section.questions ?? []);

/** Get a response by its ID (searches across all sections). */
export const getAuditResponseById = (audit: MayBe<StoredAudit>, questionId: string): MayBe<AuditResponse> =>
  getAllAuditResponses(audit).find(response => response.id === questionId);

/** Get the section that contains a specific question. */
export const getSectionByQuestionId = (audit: MayBe<StoredAudit>, questionId: string): MayBe<AuditSection> =>
  getAllSections(audit).find(section => section.questions?.some(response => response.id === questionId));

/** Get the index of a question within its section. Returns -1 if not found. */
const getQuestionIndexInSection = (section: MayBe<AuditSection>, questionId: string): number =>
  (section?.questions ?? []).findIndex(response => response.id === questionId);

/** Get the position of a question (section index and question index). */
export const getQuestionPosition = (
  audit: MayBe<StoredAudit>,
  questionId: string,
): { sectionIndex: number; questionIndex: number } | null => {
  const section = getSectionByQuestionId(audit, questionId);
  if (!section) {
    return null;
  }

  const sectionIndex = getSectionIndex(audit, section.id);
  const questionIndex = getQuestionIndexInSection(section, questionId);

  return { sectionIndex, questionIndex };
};

export type AssigneeSelection = { userId?: string | null; userGroupId?: string | null };

export const buildAssigneesRequest = (
  selection: MayBe<AssigneeSelection> | null,
  previousValue: MayBe<AssigneeSelection> | null,
): AuditPatchAssigneesRequest => {
  const { userId, userGroupId } = selection ?? {};

  if (userId == null && userGroupId == null) {
    return previousValue?.userGroupId != null ? { assignedUserGroupId: null } : { assignedUserId: null };
  }

  return userGroupId != null ? { assignedUserGroupId: userGroupId } : { assignedUserId: userId };
};

type LinkableOrigin = BasicIntervention & { status: InterventionStatus };

export const isOriginInterventionLinkable = (origin: MayBe<LinkableOrigin>): origin is LinkableOrigin => {
  if (!origin) {
    return false;
  }

  return origin.status !== InterventionStatus.DELETED && origin.status !== InterventionStatus.IN_PROGRESS;
};

type CloneNavigateState = { back: string };

type CloneNavigateStateOptions = {
  origin: CloneOrigin;
  auditId: string;
  keepLink: boolean;
  pathname: string;
};

/** Build the navigation state of the duplicated audit, whose `back` targets where the duplication started from. */
export const getCloneNavigateState = ({
  origin,
  auditId,
  keepLink,
  pathname,
}: CloneNavigateStateOptions): MayBe<CloneNavigateState> => {
  if (origin === CloneOrigin.AUDIT) {
    return { back: generatePath(PATH.AUDITS_DETAILED.path, { auditId }) };
  }

  return keepLink ? { back: pathname } : undefined;
};

type CloneParamsOptions = {
  reference: string;
  archiveOriginal: boolean;
  keepLink: boolean;
  isLinked: boolean;
};

export const buildCloneParams = ({
  reference,
  archiveOriginal,
  keepLink,
  isLinked,
}: CloneParamsOptions): Required<AuditCloneQueryParams> => ({
  reference,
  archiveOriginal,
  keepLink: isLinked && keepLink,
});

export const hasOriginIntervention = (audit: MayBe<Pick<StoredAudit, 'originIntervention'>>): boolean =>
  Boolean(audit?.originIntervention);

export const isDeletableAudit = (audit: MayBe<Partial<StoredAudit>>, userId: string, roles: string[]): boolean => {
  const status = audit?.status;

  // See https://fleetback.atlassian.net/wiki/x/rwYdAQ
  return (
    Boolean(status && DELETABLE_AUDIT_STATUSES.includes(status)) &&
    (audit?.createdBy === userId || hasAccess(roles, AUDIT_MANAGER_ROLES))
  );
};
